import { useEffect, useState } from 'react';
import { Dictionary, keyBy as _keyBy } from 'lodash';
import { CustomUnitsDefinition, Task, Task as BringgTask, WayPoint } from '@bringg/types';
import { NO_ROUTE_ID, RealtimeData } from '@bringg-frontend/routes-planner';
import {
	getRoutesPlannerWayPoint,
	getRunIdsToUpdate,
	getTaskByWayPointId,
	isTaskEventValid,
	isWayPointEventValid,
	shouldRemoveTask,
	updateTasksData,
	updateWayPointData
} from './events-helper';
import { useStores } from 'bringg-web/recipes';

const useEvents = (
	tasks: BringgTask[],
	shouldSubscribeToUpdates: boolean,
	customUnitsDefinitions?: CustomUnitsDefinition
) => {
	const [realtimeData, setRealtimeData] = useState<RealtimeData>(undefined);
	const { tasksStore } = useStores();
	const handlingUnitNames = Object.keys(customUnitsDefinitions || {});

	const subscribeToEvents = () => {
		return [
			tasksStore.onUpdate(({ current }) => onTaskUpdate(current)),
			tasksStore.onDelete(({ diff }) => onTaskDelete(diff)),
			tasksStore.onWayPointUpdate(onWayPointUpdate),
			tasksStore.onWayPointDelete(onWayPointDelete)
		];
	};

	const unsubscribe = subscriptions => {
		subscriptions.forEach(subscription => {
			subscription();
		});
	};

	useEffect(() => {
		if (!shouldSubscribeToUpdates) {
			return undefined;
		}

		const subscriptions = subscribeToEvents();

		return () => {
			unsubscribe(subscriptions);
		};
	}, []);

	const getDefaultState = () => ({ tasksById: _keyBy(tasks, 'id'), runIdsToUpdate: [] });

	const onWayPointUpdate = (wayPoint: WayPoint) => {
		setRealtimeData((oldStateData: RealtimeData) => {
			const oldState = oldStateData || getDefaultState();

			if (!isWayPointEventValid(oldState.tasksById, wayPoint)) {
				return oldState;
			}

			const updatedTasksByIds = Object.assign({}, oldState.tasksById);
			updateWayPointData(updatedTasksByIds, wayPoint);
			const runIdToUpdate = getTaskByWayPointId(updatedTasksByIds, wayPoint.id).run_id || NO_ROUTE_ID;

			return {
				tasksById: updatedTasksByIds,
				runIdsToUpdate: [runIdToUpdate]
			};
		});
	};

	const onWayPointDelete = wayPointData => {
		const taskId = wayPointData.task_id;
		const wayPointId = wayPointData.way_point_id;

		setRealtimeData((oldStateData: RealtimeData) => {
			const oldState = oldStateData || getDefaultState();

			const task = oldState.tasksById[taskId];
			if (!task) {
				return oldState;
			}

			const currentWayPoint = getRoutesPlannerWayPoint(task);

			if (!currentWayPoint || currentWayPoint.id !== wayPointId) {
				return oldState;
			}

			const runId = task.run_id ?? NO_ROUTE_ID;
			const updatedTasksByIds = removeTask(oldState.tasksById, task);

			return {
				tasksById: updatedTasksByIds,
				runIdsToUpdate: [runId]
			};
		});
	};

	const onTaskDelete = task => {
		setRealtimeData((oldStateData: RealtimeData) => {
			const oldState = oldStateData || getDefaultState();

			const taskToDelete = Object.assign({}, task);
			const runId = taskToDelete.run_id ?? NO_ROUTE_ID;
			const updatedTasksByIds = removeTask(oldState.tasksById, taskToDelete);

			return {
				tasksById: updatedTasksByIds,
				runIdsToUpdate: [runId]
			};
		});
	};

	const removeTask = (tasksById: Dictionary<Task>, updatedTask: Task): Dictionary<Task> => {
		const updatedTasksByIds = Object.assign({}, tasksById);

		if (updatedTasksByIds[updatedTask.id]) {
			delete updatedTasksByIds[updatedTask.id];
		}
		return updatedTasksByIds;
	};

	const onTaskUpdate = (task: BringgTask) => {
		setRealtimeData((oldStateData: RealtimeData) => {
			const oldState = oldStateData || { tasksById: _keyBy(tasks, 'id'), runIdsToUpdate: [] };

			const updatedTask = Object.assign({}, task);
			const currentTask = oldState && oldState.tasksById[updatedTask.id];

			if (currentTask && shouldRemoveTask(updatedTask)) {
				const runId = updatedTask.run_id ?? NO_ROUTE_ID;
				const updatedTasksByIds = removeTask(oldState.tasksById, updatedTask);

				return {
					tasksById: updatedTasksByIds,
					runIdsToUpdate: [runId]
				};
			}
			if (!isTaskEventValid(Object.values(oldState.tasksById), currentTask, updatedTask, handlingUnitNames)) {
				return oldState;
			}

			updateTasksData(currentTask, updatedTask);

			return {
				tasksById: { ...oldState.tasksById, [updatedTask.id]: updatedTask },
				runIdsToUpdate: getRunIdsToUpdate(currentTask, updatedTask)
			};
		});
	};

	return { realtimeData };
};

export default useEvents;
